import React from 'react';
import { Pagination, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useAnimalPhotos from '../../hooks/useAnimalPhotos';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import { DEFAULT_ROWS_PER_PAGE_MIDDLE } from '../../constants/constants';
import { AnimalPhotoWithUrl } from '../../models/animal-photos';
import AnimalPhotoViewer from './AnimalPhotoViewer';

export interface AnimalPhotoGalleryProps {
  animalId: string;
}

/**
 * The photos of one animal as a paged thumbnail grid, oldest first. Each thumbnail opens the
 * full-size viewer.
 */
const AnimalPhotoGallery: React.FC<AnimalPhotoGalleryProps> = ({ animalId }) => {
  const { t } = useTranslation();
  const { formatDate, formatTime } = useFarmDateTime();
  const [page, setPage] = React.useState(0);
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);
  const { photos, totalCount, isBusy, loaded } = useAnimalPhotos(animalId, page, DEFAULT_ROWS_PER_PAGE_MIDDLE);

  const pageCount = Math.ceil(totalCount / DEFAULT_ROWS_PER_PAGE_MIDDLE);
  const captured = (photo: AnimalPhotoWithUrl) => `${formatDate(photo.capturedAt)} ${formatTime(photo.capturedAt)}`;

  const renderGrid = () => {
    if (!loaded) {
      return (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} variant="rectangular" className="!aspect-square !h-auto rounded-md" />
          ))}
        </div>
      );
    }

    if (photos.length === 0) {
      return (
        <p className="text-sm text-gray-500 text-center py-10 m-0" id="animal-photos-empty">
          {t('f-animal-photos-empty', { defaultValue: 'No photos have been taken of this animal yet.' })}
        </p>
      );
    }

    return (
      <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 list-none p-0 m-0">
        {photos.map((photo, index) => (
          <li key={photo.id}>
            <button
              type="button"
              className="appearance-none bg-transparent border-0 p-0 cursor-pointer w-full text-left group"
              onClick={() => setOpenIndex(index)}
              aria-label={t('f-animal-photos-open', { captured: captured(photo) })}
            >
              <div className="aspect-square rounded-md overflow-hidden bg-gray-100 border border-gray-200 group-hover:ring-2 group-hover:ring-brand/40 transition">
                {photo.url ? (
                  <img
                    src={photo.url}
                    alt={t('f-animal-photos-alt', { captured: captured(photo) })}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-gray-500 px-2 text-center">
                    {t('f-animal-photos-unavailable', { defaultValue: 'Photo unavailable' })}
                  </div>
                )}
              </div>
              <span className="block mt-1 text-xs text-gray-600">{captured(photo)}</span>
            </button>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div id="animal-photos" className={`transition-opacity ${isBusy && loaded ? 'opacity-60' : ''}`}>
      {renderGrid()}

      {pageCount > 1 && (
        <div className="flex justify-center mt-6">
          <Pagination
            count={pageCount}
            page={page + 1}
            onChange={(_event, value) => setPage(value - 1)}
            disabled={isBusy}
            size="small"
          />
        </div>
      )}

      <AnimalPhotoViewer photos={photos} index={openIndex} onIndexChange={setOpenIndex} formatCaptured={captured} />
    </div>
  );
};

export default AnimalPhotoGallery;
